import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { CampFields, dateRange, money, readCamp, type Camp } from './CampsPage';

type Part = {
  id: string; personId: string | null; name: string; birthDate: string | null; phone: string | null; emergencyContact: string | null; medicalNotes: string | null; guardian: string | null;
  status: 'inscrito' | 'confirmado' | 'cancelado'; paidCents: number; balanceCents: number; vehicle: { id: string; name: string; driver: string | null } | null; tent: { id: string; name: string } | null; mine: boolean;
  payments?: { amountCents: number; method: string; note: string | null; paidAt: string }[];
};
type Item = { id: string; area: 'cocina' | 'actividad' | 'equipo' | 'compra'; day: string | null; slot: string | null; title: string; details: string | null; responsible: string | null; amountCents: number | null; done: boolean };
type Detail = Camp & {
  canManage: boolean; enrolled: number; mine: Part[]; canPayOnline?: boolean;
  management: null | {
    participants: Part[];
    vehicles: { id: string; name: string; driver: string | null; phone: string | null; capacity: number; notes: string | null; occupied: number }[];
    tents: { id: string; name: string; capacity: number; responsible: string | null; equipment: string | null; occupied: number }[];
    items: Item[];
    totals: { enrolled: number; paidCents: number; pendingCents: number; purchasesCents: number; withMedicalNotes: number };
  };
};
const AREAS: [Item['area'], string, string][] = [
  ['cocina', 'Cocina', 'Menú por día y comida: platillo, cantidades y quién cocina.'],
  ['actividad', 'Actividades', 'Programa por día y hora.'],
  ['equipo', 'Equipos', 'Seguridad, primeros auxilios, infraestructura… y quién está a cargo.'],
  ['compra', 'Compras', 'Lo que hay que comprar y cuánto cuesta.'],
];
/** Cocina, Actividades y Compras van juntas en «Logística», cada una como sección. */
const LOGISTICS: Item['area'][] = ['cocina', 'actividad', 'compra'];
const STATUS = { inscrito: 'Inscrito', confirmado: 'Confirmado', cancelado: 'Cancelado' } as const;

/** Una edición del campamento. */
export function CampPage() {
  const { campId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/calendar/churches/${churchId}`;
  const data = useAsync((signal) => (churchId ? api.get<Detail>(`${base}/camps/${campId}`, signal) : Promise.resolve(null)), [base, campId]);
  const [params, setParams] = useSearchParams();
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const c = data.data;
  if (!c) return null;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const tabs: [string, string][] = c.management
    ? [['resumen', 'Resumen'], ['participantes', 'Participantes'], ['transporte', 'Transporte'], ['carpas', 'Carpas'], ['logistica', 'Logística'], ['equipo', 'Equipos']]
    : [['resumen', 'Resumen']];
  // Las direcciones de antes (?ver=cocina, actividad o compra) abren Logística.
  const asked = params.get('ver');
  const tab = tabs.find(([k]) => k === (LOGISTICS.includes(asked as Item['area']) ? 'logistica' : asked))?.[0] ?? 'resumen';
  const m = c.management;
  const payResult = params.get('pago');
  async function payOnline(participantId: string) {
    setBusy(true);
    setMsg(null);
    try {
      const { url } = await api.post<{ url: string }>(`${base}/camp-participants/${participantId}/checkout`, {});
      window.location.assign(url);
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <p className="small">
        <Link to="/app/campamentos">← Campamentos</Link>
      </p>
      <header className="page__header">
        <h1>{c.name}</h1>
        <p className="muted">
          {dateRange(c.startsOn, c.endsOn)}
          {c.placeName ? ` · ${c.placeName}` : ''} · {c.priceCents ? money(c.priceCents) : 'Sin costo'} · {c.enrolled} inscritos{c.capacity ? ` de ${c.capacity}` : ''}
        </p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {payResult === 'aprobado' ? <Alert tone="success">¡Gracias! Tu pago fue aprobado. En unos segundos se verá aquí.</Alert> : null}
      {payResult === 'pendiente' ? <Alert tone="info">Tu pago quedó pendiente (por ejemplo, en OXXO). Se registrará en cuanto se confirme.</Alert> : null}
      {payResult === 'rechazado' ? <Alert>No se pudo cobrar. Intenta de nuevo con otro medio de pago.</Alert> : null}
      {tabs.length > 1 ? (
        <div className="tabs tabs--scroll" role="tablist" aria-label="Campamento">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams(k === 'resumen' ? {} : { ver: k }, { replace: true })}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === 'resumen' ? (
        <>
          {m ? (
            <div className="stats">
              <div><strong>{m.totals.enrolled}</strong><span>inscritos</span></div>
              <div><strong>{money(m.totals.paidCents)}</strong><span>cobrado</span></div>
              <div><strong>{money(m.totals.pendingCents)}</strong><span>por cobrar</span></div>
              <div><strong>{money(m.totals.purchasesCents)}</strong><span>en compras</span></div>
              <div><strong>{m.totals.withMedicalNotes}</strong><span>con alergias o notas médicas</span></div>
            </div>
          ) : null}
          {c.description ? <Card><p className="pre-line">{c.description}</p></Card> : null}
          {c.address || c.mapsUrl ? (
            <Card title="Dónde">
              {c.address ? <p>{c.address}</p> : null}
              {c.mapsUrl ? <a className="btn btn--secondary" href={c.mapsUrl} target="_blank" rel="noopener noreferrer">Cómo llegar</a> : null}
            </Card>
          ) : null}
          <Card title="Mis inscripciones">
            {c.mine.length === 0 ? <p className="muted small">Aún no inscribes a nadie.</p> : null}
            <ul className="list">
              {c.mine.map((p) => (
                <li key={p.id} className="list__item">
                  <span className="agenda__body">
                    <strong>{p.name}</strong>
                    <span className="small muted">
                      {STATUS[p.status]} · Pagado {money(p.paidCents)}{p.balanceCents ? ` · Falta ${money(p.balanceCents)}` : ' · ¡Pagado!'}
                      {p.vehicle ? ` · Viaja en: ${p.vehicle.name}${p.vehicle.driver ? ` (${p.vehicle.driver})` : ''}` : ''}
                      {p.tent ? ` · Carpa: ${p.tent.name}` : ''}
                    </span>
                  </span>
                  {p.status !== 'cancelado' && p.balanceCents > 0 && c.canPayOnline ? (
                    <Button busy={busy} onClick={() => void payOnline(p.id)}>
                      Pagar en línea
                    </Button>
                  ) : null}
                  {p.status !== 'cancelado' ? (
                    <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`¿Cancelar la inscripción de ${p.name}?`)) void run(() => api.put(`${base}/camp-participants/${p.id}`, { ...p, status: 'cancelado' }), 'Inscripción cancelada.'); }}>
                      Cancelar
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {c.registrationOpen || c.canManage ? <RegisterForm busy={busy} hasSelf={c.mine.some((p) => p.personId === me.personId && p.status !== 'cancelado')} onSave={(b) => run(() => api.post(`${base}/camps/${c.id}/participants`, b), '¡Inscripción lista!')} /> : <p className="small muted">Las inscripciones están cerradas.</p>}
          </Card>
          {c.canManage ? (
            <details className="pastoral-edit">
              <summary>Editar datos</summary>
              <form className="form" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); void run(() => api.put(`${base}/camps/${c.id}`, readCamp(new FormData(e.currentTarget))), 'Campamento actualizado.'); }}>
                <CampFields camp={c} />
                <Button type="submit" busy={busy}>Guardar</Button>
              </form>
            </details>
          ) : null}
        </>
      ) : null}

      {tab === 'participantes' && m ? (
        <Card title={`Participantes (${m.totals.enrolled})`}>
          <ul className="list">
            {m.participants.map((p) => (
              <li key={p.id} className="list__item camp-part">
                <span className="agenda__body">
                  <strong>{p.name}</strong>
                  <span className="small muted">
                    {STATUS[p.status]}{p.birthDate ? ` · Nació ${p.birthDate.split('-').reverse().join('/')}` : ''}{p.guardian ? ` · Tutor: ${p.guardian}` : ''}{p.phone ? ` · ${p.phone}` : ''}{p.emergencyContact ? ` · Emergencia: ${p.emergencyContact}` : ''}
                  </span>
                  {p.medicalNotes ? <span className="small camp-part__medical">⚕ {p.medicalNotes}</span> : null}
                  <span className="small">Pagado {money(p.paidCents)}{p.balanceCents ? ` · Falta ${money(p.balanceCents)}` : ' · Pagado completo'}</span>
                </span>
                <span className="actions">
                  <select aria-label={`Estado de ${p.name}`} value={p.status} disabled={busy} onChange={(e) => void run(() => api.put(`${base}/camp-participants/${p.id}`, { ...p, status: e.target.value }), 'Estado actualizado.')}>
                    {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <PaymentForm name={p.name} busy={busy} onSave={(b) => run(() => api.post(`${base}/camp-participants/${p.id}/payments`, b), 'Pago registrado. También quedó en Finanzas, en la cuenta «Campamento» (salvo las becas).')} />
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {tab === 'transporte' && m ? (
        <PlacementTab kind="vehicles" title="Vehículos" m={m} base={`${base}/camps/${c.id}`} run={run} busy={busy} placeBase={base} />
      ) : null}
      {tab === 'carpas' && m ? <PlacementTab kind="tents" title="Carpas" m={m} base={`${base}/camps/${c.id}`} run={run} busy={busy} placeBase={base} /> : null}
      {AREAS.map(([area, label, hint]) => ((tab === area || (tab === 'logistica' && LOGISTICS.includes(area))) && m ? <AreaTab key={area} area={area} label={label} hint={hint} items={m.items.filter((i) => i.area === area)} base={`${base}/camps/${c.id}`} run={run} busy={busy} days={[c.startsOn, c.endsOn]} /> : null))}
    </div>
  );
}

function RegisterForm({ busy, hasSelf, onSave }: { busy: boolean; hasSelf: boolean; onSave: (b: object) => Promise<boolean> }) {
  const [forMe, setForMe] = useState(!hasSelf);
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        const t = (k: string) => String(f.get(k) ?? '').trim() || null;
        void onSave({ forMe, name: t('name') ?? '', birthDate: t('birthDate'), phone: t('phone'), emergencyContact: t('emergencyContact'), medicalNotes: t('medicalNotes'), guardian: t('guardian') }).then((ok) => ok && (e.target as HTMLFormElement).reset());
      }}
    >
      <div className="segmented" role="radiogroup" aria-label="¿A quién inscribes?">
        {!hasSelf ? <label className="check"><input type="radio" checked={forMe} onChange={() => setForMe(true)} /> A mí</label> : null}
        <label className="check"><input type="radio" checked={!forMe} onChange={() => setForMe(false)} /> A otra persona (hijo, familiar…)</label>
      </div>
      {!forMe ? (
        <div className="form__row">
          <Field label="Nombre completo" name="name" required maxLength={150} />
          <Field label="Fecha de nacimiento" name="birthDate" type="date" />
        </div>
      ) : null}
      {!forMe ? <Field label="Tutor responsable" name="guardian" maxLength={150} /> : null}
      <div className="form__row">
        <Field label="Teléfono" name="phone" type="tel" maxLength={30} />
        <Field label="Contacto de emergencia" name="emergencyContact" maxLength={200} placeholder="Nombre y teléfono" />
      </div>
      <Field label="Alergias o notas médicas (opcional)" name="medicalNotes" maxLength={1000} />
      <Button type="submit" busy={busy}>Inscribir</Button>
    </form>
  );
}

function PaymentForm({ name, busy, onSave }: { name: string; busy: boolean; onSave: (b: object) => Promise<boolean> }) {
  const [open, setOpen] = useState(false);
  if (!open) return <Button variant="secondary" onClick={() => setOpen(true)}>Registrar pago</Button>;
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        void onSave({ amountCents: Math.round(Number(f.get('amount') ?? 0) * 100), method: String(f.get('method')), note: String(f.get('note') ?? '').trim() || null }).then((ok) => ok && setOpen(false));
      }}
    >
      <input aria-label={`Monto del pago de ${name}`} name="amount" type="number" step="0.01" min="0.01" required placeholder="Monto" />
      <select aria-label="Forma de pago" name="method" defaultValue="efectivo">
        {['efectivo', 'transferencia', 'tarjeta', 'beca', 'otro'].map((x) => <option key={x} value={x}>{x.charAt(0).toUpperCase() + x.slice(1)}</option>)}
      </select>
      <input aria-label="Nota" name="note" maxLength={300} placeholder="Nota" />
      <Button type="submit" busy={busy}>Guardar</Button>
    </form>
  );
}

function PlacementTab({ kind, title, m, base, placeBase, run, busy }: { kind: 'vehicles' | 'tents'; title: string; m: NonNullable<Detail['management']>; base: string; placeBase: string; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean>; busy: boolean }) {
  const list = kind === 'vehicles' ? m.vehicles : m.tents;
  const active = m.participants.filter((p) => p.status !== 'cancelado');
  return (
    <>
      <Card title={title}>
        {list.length === 0 ? <p className="muted small">Todavía no hay {title.toLowerCase()}.</p> : null}
        <ul className="list">
          {list.map((x) => (
            <li key={x.id} className="list__item">
              <span className="agenda__body">
                <strong>{x.name}</strong>
                <span className="small muted">
                  {x.occupied} de {x.capacity}
                  {'driver' in x && x.driver ? ` · Conduce ${x.driver}` : ''}{'phone' in x && x.phone ? ` · ${x.phone}` : ''}
                  {'responsible' in x && x.responsible ? ` · Responsable ${x.responsible}` : ''}{'equipment' in x && x.equipment ? ` · ${x.equipment}` : ''}
                </span>
                <span className="small">{active.filter((p) => (kind === 'vehicles' ? p.vehicle?.id : p.tent?.id) === x.id).map((p) => p.name).join(', ')}</span>
              </span>
              <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/${kind}/${x.id}`), 'Quitado.')}>Quitar</Button>
            </li>
          ))}
        </ul>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const t = (k: string) => String(f.get(k) ?? '').trim() || null;
            const body = kind === 'vehicles'
              ? { name: t('name'), driver: t('driver'), phone: t('phone'), capacity: Number(t('capacity') ?? 1), notes: null }
              : { name: t('name'), capacity: Number(t('capacity') ?? 1), responsible: t('responsible'), equipment: t('equipment') };
            void run(() => api.post(`${base}/${kind}`, body), 'Agregado.').then((ok) => ok && (e.target as HTMLFormElement).reset());
          }}
        >
          <div className="form__row form__row--3">
            <Field label={kind === 'vehicles' ? 'Vehículo' : 'Carpa'} name="name" required maxLength={120} placeholder={kind === 'vehicles' ? 'Camioneta blanca' : 'Carpa 1'} />
            <Field label="Lugares" name="capacity" type="number" min={1} max={100} defaultValue={kind === 'vehicles' ? 8 : 6} required />
            {kind === 'vehicles' ? <Field label="Conductor" name="driver" maxLength={120} /> : <Field label="Responsable" name="responsible" maxLength={120} />}
          </div>
          {kind === 'vehicles' ? <Field label="Teléfono del conductor" name="phone" maxLength={30} /> : <Field label="Equipo (lámparas, bolsas…)" name="equipment" maxLength={500} />}
          <Button type="submit" busy={busy}>Agregar</Button>
        </form>
      </Card>
      <Card title="Asignar participantes">
        <ul className="list">
          {active.map((p) => (
            <li key={p.id} className="list__item food-item">
              <span className="checklist__body">{p.name}</span>
              <select
                aria-label={`${kind === 'vehicles' ? 'Vehículo' : 'Carpa'} de ${p.name}`}
                value={(kind === 'vehicles' ? p.vehicle?.id : p.tent?.id) ?? ''}
                disabled={busy}
                onChange={(e) => void run(() => api.put(`${placeBase}/camp-participants/${p.id}/placement`, { vehicleId: kind === 'vehicles' ? e.target.value || null : (p.vehicle?.id ?? null), tentId: kind === 'tents' ? e.target.value || null : (p.tent?.id ?? null) }), 'Asignado.')}
              >
                <option value="">Sin asignar</option>
                {list.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.occupied}/{x.capacity})</option>)}
              </select>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function AreaTab({ area, label, hint, items, base, run, busy, days }: { area: Item['area']; label: string; hint: string; items: Item[]; base: string; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean>; busy: boolean; days: [string, string] }) {
  const dated = area === 'cocina' || area === 'actividad';
  return (
    <Card title={label}>
      <p className="small muted">{hint}</p>
      <ul className="checklist">
        {items.map((i) => (
          <li key={i.id} className="checklist__item food-item">
            <label className="check checklist__body">
              <input type="checkbox" checked={i.done} disabled={busy} onChange={() => void run(() => api.put(`${base}/items/${i.id}`, { ...i, done: !i.done }), 'Listo.')} />
              <span>
                <strong className={i.done ? 'muted' : ''}>{i.title}</strong>
                <span className="small muted block">
                  {[i.day ? i.day.split('-').reverse().join('/') : null, i.slot, i.responsible ? `A cargo: ${i.responsible}` : null, i.amountCents ? money(i.amountCents) : null].filter(Boolean).join(' · ')}
                </span>
                {i.details ? <span className="small block">{i.details}</span> : null}
              </span>
            </label>
            <Button variant="ghost" busy={busy} onClick={() => void run(() => api.delete(`${base}/items/${i.id}`), 'Quitado.')}>Quitar</Button>
          </li>
        ))}
      </ul>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          void run(() => api.post(`${base}/items`, { area, day: t('day'), slot: t('slot'), title: t('title'), details: t('details'), responsible: t('responsible'), amountCents: t('amount') ? Math.round(Number(t('amount')) * 100) : null }), 'Agregado.').then((ok) => ok && (e.target as HTMLFormElement).reset());
        }}
      >
        <Field label={area === 'cocina' ? 'Platillo' : area === 'actividad' ? 'Actividad' : area === 'equipo' ? 'Equipo o tarea' : 'Qué comprar'} name="title" required maxLength={200} />
        {dated ? (
          <div className="form__row">
            <Field label="Día" name="day" type="date" min={days[0]} max={days[1]} />
            <Field label={area === 'cocina' ? 'Comida' : 'Hora'} name="slot" maxLength={40} placeholder={area === 'cocina' ? 'Desayuno, comida, cena' : '10:00'} />
          </div>
        ) : null}
        <div className="form__row">
          <Field label="Responsable" name="responsible" maxLength={120} />
          {area === 'compra' ? <Field label="Costo (MXN)" name="amount" type="number" min={0} step="0.01" /> : <Field label={area === 'cocina' ? 'Ingredientes y cantidades' : 'Detalles'} name="details" maxLength={1000} />}
        </div>
        <Button type="submit" busy={busy}>Agregar</Button>
      </form>
    </Card>
  );
}
